import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function isStandalone() {
  // iOS Safari supports Web Push only when installed to Home Screen
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  return { isIOS, isAndroid };
}

export function usePushNotifications() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [registration, setRegistration] = useState(null);

  const { isIOS } = detectPlatform();
  const standalone = isStandalone();
  // iOS requires installation to Home. On other platforms Push works in browser too.
  const canSubscribe = supported && (!isIOS || standalone);

  // Load current state
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        setRegistration(reg);
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (_e) {
        // ignore
      }
    })();
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!canSubscribe) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "denied" };

      const reg = registration || (await navigator.serviceWorker.ready);
      // Fetch public key
      const r = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(r.data.publicKey);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: "error", error: e };
    } finally {
      setBusy(false);
    }
  }, [canSubscribe, registration]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = registration || (await navigator.serviceWorker.ready);
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch (_e) {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported, registration]);

  return {
    supported,
    canSubscribe,
    standalone,
    isIOS,
    permission,
    subscribed,
    busy,
    subscribe,
    unsubscribe,
  };
}
